import { useState } from "react";
import { Link, useLocation } from "wouter";
import { emitTourEvent } from "@/lib/tour-events";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListProjects,
  useCreateProject,
  getListProjectsQueryKey,
  useListCustomers,
  type ProjectStatus,
  type ProjectListItem,
} from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/hooks/use-page-title";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { ProTeaserSection } from "@/components/pro-gate";
import { Plus, FolderKanban, Search } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTargetMargin, TONE_HEX } from "@/hooks/use-target-margin";

const STATUS_LABELS: Record<ProjectStatus, string> = {
  lead: "Lead",
  quoted: "Quoted",
  approved: "Approved",
  in_progress: "In Progress",
  complete: "Complete",
  archived: "Archived",
};

const STATUS_COLORS: Record<ProjectStatus, string> = {
  lead: "#94a3b8",
  quoted: "#3b82f6",
  approved: "#8b5cf6",
  in_progress: "#f59e0b",
  complete: "#10b981",
  archived: "#6b7280",
};

function formatMoney(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}


export default function ProjectsListPage() {
  usePageTitle("Projects");
  const { user } = useAuth();
  const { hasProAccess: isPro } = getTrialInfo(user);

  if (!isPro) {
    return (
      <div>
        <div style={{ marginBottom: "1.5rem" }}>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FolderKanban style={{ width: "1.75rem", height: "1.75rem", color: "hsl(var(--primary))" }} />
            Projects
          </h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
            Group quotes, invoices and real costs for each job.
          </p>
        </div>
        <ProTeaserSection
          title="Projects"
          description="Group quotes, invoices, and real costs for each job — then see quoted vs. actual at a glance."
        >
          <div style={{ display: "grid", gap: "0.75rem" }}>
            {[
              { name: "Smith bathroom remodel", customer: "Smith Family", status: "in_progress" as ProjectStatus },
              { name: "Lakeside deck build", customer: "Johnson Residence", status: "approved" as ProjectStatus },
              { name: "Office repaint", customer: "Acme Co.", status: "complete" as ProjectStatus },
            ].map((p, i) => (
              <Card key={i}>
                <CardContent style={{ padding: "1rem", display: "grid", gridTemplateColumns: "1.6fr 0.9fr repeat(7, 1fr)", gap: "0.75rem", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "1rem" }}>{p.name}</div>
                    <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginTop: 2 }}>{p.customer}</div>
                  </div>
                  <div>
                    <span style={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      padding: "0.25rem 0.5rem",
                      borderRadius: "0.375rem",
                      background: STATUS_COLORS[p.status] + "22",
                      color: STATUS_COLORS[p.status],
                    }}>{STATUS_LABELS[p.status]}</span>
                  </div>
                  <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Quoted</div><div style={{ fontWeight: 600 }}>$12,400</div></div>
                  <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Actual</div><div style={{ fontWeight: 600 }}>$9,800</div></div>
                  <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Invoiced</div><div style={{ fontWeight: 600 }}>$8,000</div></div>
                  <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Paid</div><div style={{ fontWeight: 600 }}>$8,000</div></div>
                  <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Outstanding</div><div style={{ fontWeight: 600 }}>$0</div></div>
                  <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Profit</div><div style={{ fontWeight: 600, color: "#10b981" }}>$2,600</div></div>
                  <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Margin</div><div style={{ fontWeight: 700, color: "#10b981" }}>26.5%</div></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ProTeaserSection>
      </div>
    );
  }

  return <ProjectsListContent />;
}

function ProjectsListContent() {
  const [, navigate] = useLocation();
  // Margin colours follow the target margin in Settings.
  const { tone } = useTargetMargin();
  const marginColor = (margin: number) => TONE_HEX[tone(margin)];
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | "all">("all");
  const [customerFilter, setCustomerFilter] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [showNew, setShowNew] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("new") === "1";
  });
  const [name, setName] = useState("");
  const [customerId, setCustomerId] = useState<string>("");
  const [newStatus, setNewStatus] = useState<ProjectStatus>("lead");
  const [newStartDate, setNewStartDate] = useState("");
  const [newTargetDate, setNewTargetDate] = useState("");
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data: projects, isLoading } = useListProjects({
    search: search || undefined,
    status: statusFilter === "all" ? undefined : statusFilter,
    customerId: customerFilter === "all" ? undefined : Number(customerFilter),
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
  });
  const { data: customers } = useListCustomers();
  const createProject = useCreateProject({
    onSuccess: (project) => {
      toast({ title: "Project created" });
      qc.invalidateQueries({ queryKey: getListProjectsQueryKey() });
      emitTourEvent("project.created");
      setShowNew(false);
      setName("");
      setCustomerId("");
      setNewStatus("lead");
      setNewStartDate("");
      setNewTargetDate("");
      navigate(`/projects/${project.id}`);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : "Failed";
      toast({ title: "Failed to create project", description: msg, variant: "destructive" });
    },
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <FolderKanban style={{ width: "1.75rem", height: "1.75rem", color: "hsl(var(--primary))" }} />
            Projects
          </h1>
          <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
            Group quotes, invoices and real costs for each job.
          </p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button><Plus style={{ width: 16, height: 16, marginRight: 6 }} /> New Project</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Project</DialogTitle>
            </DialogHeader>
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Smith bathroom remodel" />
              </div>
              <div>
                <Label>Customer (optional)</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger><SelectValue placeholder="Select a customer" /></SelectTrigger>
                  <SelectContent>
                    {(customers ?? []).map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={newStatus} onValueChange={(v) => setNewStatus(v as ProjectStatus)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(Object.keys(STATUS_LABELS) as ProjectStatus[]).map((s) => (
                      <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                <div>
                  <Label>Start date (optional)</Label>
                  <Input type="date" value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)} />
                </div>
                <div>
                  <Label>Target completion (optional)</Label>
                  <Input type="date" value={newTargetDate} onChange={(e) => setNewTargetDate(e.target.value)} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button
                disabled={!name.trim() || createProject.isPending}
                onClick={() => createProject.mutate({
                  name: name.trim(),
                  customerId: customerId ? Number(customerId) : null,
                  status: newStatus,
                  startDate: newStartDate || null,
                  targetCompletionDate: newTargetDate || null,
                })}
              >Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240, position: "relative" }}>
          <Search style={{ position: "absolute", left: 10, top: 10, width: 16, height: 16, color: "hsl(var(--muted-foreground))" }} />
          <Input style={{ paddingLeft: 32 }} placeholder="Search projects..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as ProjectStatus | "all")}>
          <SelectTrigger style={{ width: 160 }}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {(Object.keys(STATUS_LABELS) as ProjectStatus[]).map((s) => (
              <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={customerFilter} onValueChange={setCustomerFilter}>
          <SelectTrigger style={{ width: 200 }}><SelectValue placeholder="All customers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All customers</SelectItem>
            {(customers ?? []).map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          style={{ width: 160 }}
          aria-label="Start date from"
        />
        <Input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          style={{ width: 160 }}
          aria-label="Start date to"
        />
        {(fromDate || toDate || customerFilter !== "all") && (
          <Button variant="ghost" size="sm" onClick={() => { setFromDate(""); setToDate(""); setCustomerFilter("all"); }}>
            Clear filters
          </Button>
        )}
      </div>

      {isLoading ? (
        <p style={{ padding: "2rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>Loading…</p>
      ) : !projects || projects.length === 0 ? (
        <Card>
          <CardContent style={{ padding: "3rem", textAlign: "center" }}>
            <FolderKanban style={{ width: 48, height: 48, margin: "0 auto 1rem", color: "hsl(var(--muted-foreground))" }} />
            <h2 style={{ fontSize: "1.125rem", fontWeight: 600, marginBottom: "0.5rem" }}>No projects yet</h2>
            <p style={{ color: "hsl(var(--muted-foreground))", marginBottom: "1rem" }}>
              Create a project to track quotes, invoices, and real costs in one place.
            </p>
            <Button onClick={() => setShowNew(true)}><Plus style={{ width: 16, height: 16, marginRight: 6 }} /> New Project</Button>
          </CardContent>
        </Card>
      ) : (
        <div style={{ display: "grid", gap: "0.75rem" }}>
          {projects.map((p: ProjectListItem) => (
            <Link key={p.id} href={`/projects/${p.id}`}>
              <Card style={{ cursor: "pointer", transition: "border-color 0.15s" }}>
                {isMobile ? (
                  <CardContent style={{ padding: "0.875rem 1rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", marginBottom: "0.25rem" }}>
                      <div style={{ fontWeight: 600, fontSize: "1rem", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                      <span style={{
                        fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.5rem",
                        borderRadius: "0.375rem", flexShrink: 0,
                        background: STATUS_COLORS[p.status] + "22", color: STATUS_COLORS[p.status],
                      }}>{STATUS_LABELS[p.status]}</span>
                    </div>
                    <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.625rem" }}>
                      {p.customerName ?? "No customer"}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.5rem" }}>
                      {[
                        { label: "Quoted", value: formatMoney(p.quotedTotal) },
                        { label: "Paid", value: formatMoney(p.paidTotal) },
                        { label: "Margin", value: `${p.margin.toFixed(1)}%`, color: marginColor(p.margin) },
                      ].map(({ label, value, color }) => (
                        <div key={label}>
                          <div style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", marginBottom: 2 }}>{label}</div>
                          <div style={{ fontWeight: 600, fontSize: "0.875rem", color }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                ) : (
                  <CardContent style={{ padding: "1rem", display: "grid", gridTemplateColumns: "1.6fr 0.9fr repeat(7, 1fr)", gap: "0.75rem", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: "1rem" }}>{p.name}</div>
                      <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginTop: 2 }}>
                        {p.customerName ?? "No customer"}
                      </div>
                    </div>
                    <div>
                      <span style={{
                        fontSize: "0.75rem", fontWeight: 600, padding: "0.25rem 0.5rem",
                        borderRadius: "0.375rem",
                        background: STATUS_COLORS[p.status] + "22", color: STATUS_COLORS[p.status],
                      }}>{STATUS_LABELS[p.status]}</span>
                    </div>
                    <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Quoted</div><div style={{ fontWeight: 600 }}>{formatMoney(p.quotedTotal)}</div></div>
                    <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Actual</div><div style={{ fontWeight: 600 }}>{formatMoney(p.actualTotal)}</div></div>
                    <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Invoiced</div><div style={{ fontWeight: 600 }}>{formatMoney(p.invoicedTotal)}</div></div>
                    <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Paid</div><div style={{ fontWeight: 600 }}>{formatMoney(p.paidTotal)}</div></div>
                    <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Outstanding</div><div style={{ fontWeight: 600 }}>{formatMoney(p.outstanding)}</div></div>
                    <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Profit</div><div style={{ fontWeight: 600, color: marginColor(p.margin) }}>{formatMoney(p.profit)}</div></div>
                    <div><div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>Margin</div><div style={{ fontWeight: 700, color: marginColor(p.margin) }}>{p.margin.toFixed(1)}%</div></div>
                  </CardContent>
                )}
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
